import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Header } from '../components/ui/header';
import { Footer } from '../components/ui/footer';
import { HeroSection } from '../components/ui/hero-section';
import { LogoCarousel } from '../components/ui/logo-carousel';
import { ManualAdCarousel } from '../components/ui/manual-ad-carousel';
import { USPGrid } from '../components/ui/usp-grid';
import { FieldTagsSection } from '../components/ui/field-tags-section';
import { HorizontalCarousel } from '../components/ui/horizontal-carousel';
import { AutoCarousel } from '../components/ui/auto-carousel';
import { UniversityCard } from '../components/ui/university-card';
import { ProgramCard } from '../components/ui/program-card';
import { ScholarshipTagDiscovery } from '../components/ui/scholarship-tag-discovery';
import { BlogPostCarousel } from '../components/ui/blog-post-carousel';
import { FinalCTASection } from '../components/ui/final-cta-section';
import { WhatsAppCTA } from '../components/ui/whatsapp-cta';
import { renderTipTapToHTML } from '../utils/tiptapRenderer';
import { API_BASE } from '../config/api';
import { getDefaultHomepageMeta } from '../utils/socialMeta';
import { getTitlePlainText } from '../utils/titleFormat';

const Home = () => {
  const [blogPosts, setBlogPosts] = useState([]);
  const [homepageSettings, setHomepageSettings] = useState(null);
  const [featuredUniversities, setFeaturedUniversities] = useState([]);
  const [featuredPrograms, setFeaturedPrograms] = useState([]);
  const [universitiesMap, setUniversitiesMap] = useState({});
  const [loading, setLoading] = useState(true);
  const backendUrl = API_BASE;

  useEffect(() => {
    const fetchHomepageData = async () => {
      try {
        // Parallel fetch: homepage settings and blogs (both always needed)
        const [settingsResponse, blogsResponse] = await Promise.all([
          fetch(`${backendUrl}/api/public/homepage-settings`),
          fetch(`${backendUrl}/api/public/blogs`)
        ]);

        // Process blogs (always needed)
        if (blogsResponse.ok) {
          const blogsData = await blogsResponse.json();
          const allBlogs = blogsData.blogs || [];
          const featured = allBlogs.filter(blog => blog.is_featured);
          const blogsToShow = featured.length > 0 ? featured : allBlogs;
          
          const transformedPosts = blogsToShow.slice(0, 4).map(blog => ({
            imageUrl: blog.featured_image || '/placeholders/blog-featured.svg',
            title: getTitlePlainText(blog.title),
            category: blog.category || 'General',
            href: `/blogs/${blog.slug}`
          }));
          
          setBlogPosts(transformedPosts);
        }

        // Process homepage settings
        if (settingsResponse.ok) {
          const settingsData = await settingsResponse.json();
          setHomepageSettings(settingsData.settings);
          
          const { settings } = settingsData;
          
          // Parallel fetch: universities and programs if both enabled
          const fetchPromises = [];
          
          if (settings.featured_universities?.enabled) {
            fetchPromises.push(
              fetch(`${backendUrl}/api/public/universities`)
                .then(r => r.ok ? r.json() : null)
                .then(data => ({ type: 'universities', data }))
            );
          }
          
          if (settings.featured_programs?.enabled) {
            fetchPromises.push(
              fetch(`${backendUrl}/api/public/programs`)
                .then(r => r.ok ? r.json() : null)
                .then(data => ({ type: 'programs', data }))
            );
          }
          
          // Wait for all conditional fetches in parallel
          if (fetchPromises.length > 0) {
            const results = await Promise.all(fetchPromises);
            
            results.forEach(result => {
              if (!result || !result.data) return;
              
              if (result.type === 'universities') {
                const allUniversities = result.data.universities || [];
                const maxItems = settings.featured_universities.max_items || 6;
                const featuredIds = settings.featured_universities.featured_ids || [];
                
                let featured;
                if (featuredIds.length > 0) {
                  featured = allUniversities.filter(uni => featuredIds.includes(uni.id));
                } else {
                  const featuredByFlag = allUniversities.filter(uni => uni.featured === true);
                  featured = featuredByFlag.length > 0 ? featuredByFlag : allUniversities;
                }
                setFeaturedUniversities(featured.slice(0, maxItems));
                
                // Create a map of university_id to university name for programs
                const uniMap = {};
                allUniversities.forEach(uni => {
                  uniMap[uni.id] = uni.name || uni.display_name;
                });
                setUniversitiesMap(uniMap);
              } else if (result.type === 'programs') {
                const allPrograms = result.data.programs || [];
                const maxItems = settings.featured_programs.max_items || 8;
                const featuredIds = settings.featured_programs.featured_ids || [];
                
                let featured;
                if (featuredIds.length > 0) {
                  featured = allPrograms.filter(program => featuredIds.includes(program.id));
                } else {
                  featured = allPrograms;
                }
                setFeaturedPrograms(featured.slice(0, maxItems));
              }
            });
          }
        }
      } catch (err) {
        console.error('Failed to fetch homepage data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchHomepageData();
  }, [backendUrl]);

  if (loading || !homepageSettings) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#FFFDFB' }}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-[#1E40AF] mx-auto mb-4"></div>
          <p className="text-[#6B7280]">Loading...</p>
        </div>
      </div>
    );
  }

  // Helper function to safely render TipTap JSON to HTML
  const renderTipTapContent = (content) => {
    if (!content || Object.keys(content).length === 0) {
      return '';
    }
    return renderTipTapToHTML(content);
  };

  // Get default meta for homepage
  const defaultMeta = getDefaultHomepageMeta(homepageSettings);

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#FFFDFB' }}>
      {/* Default Open Graph & Twitter Card Meta for Homepage */}
      <Helmet>
        <title>{defaultMeta.title}</title>
        <meta name="description" content={defaultMeta.description} />
        <link rel="canonical" href={defaultMeta.url} />
        
        {/* Open Graph Tags */}
        <meta property="og:title" content={defaultMeta.title} />
        <meta property="og:description" content={defaultMeta.description} />
        <meta property="og:image" content={defaultMeta.image} />
        <meta property="og:url" content={defaultMeta.url} />
        <meta property="og:type" content={defaultMeta.type} />
        <meta property="og:site_name" content={defaultMeta.siteName} />
        
        {/* Twitter Card Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={defaultMeta.title} />
        <meta name="twitter:description" content={defaultMeta.description} />
        <meta name="twitter:image" content={defaultMeta.image} />
      </Helmet>
      
      <Header />
      
      {/* Hero Section */}
      {homepageSettings.hero?.enabled && (
        <HeroSection
          headline={renderTipTapContent(homepageSettings.hero.headline)}
          description={renderTipTapContent(homepageSettings.hero.description)}
          primaryCtaText={homepageSettings.hero.primary_cta_text}
          primaryCtaHref={homepageSettings.hero.primary_cta_url}
          secondaryCtaText={homepageSettings.hero.secondary_cta_text}
          secondaryCtaHref={homepageSettings.hero.secondary_cta_url}
          sliderImages={homepageSettings.hero.slider_images}
        />
      )}

      {/* Partner Logos */}
      {homepageSettings.partner_logos?.enabled && homepageSettings.partner_logos.logos?.length > 0 && (
        <section className="py-8" style={{ backgroundColor: '#FBF9F7' }}>
          <div className="max-w-[1280px] mx-auto px-6 lg:px-10">
            <p className="text-center text-[#5A6978] mb-3" style={{ fontSize: '1.2rem', fontWeight: 650, letterSpacing: '0.025em' }}>
              {homepageSettings.partner_logos.heading}
            </p>
            <LogoCarousel logos={homepageSettings.partner_logos.logos} scrollSpeed={homepageSettings.partner_logos.scroll_speed || 30} />
          </div>
        </section>
      )}

      {/* Ad Banners */}
      {homepageSettings.ad_banners?.enabled && homepageSettings.ad_banners.items?.length > 0 && (
        <section className="py-4">
          <div className="max-w-[1280px] mx-auto px-6 lg:px-10">
            <ManualAdCarousel items={homepageSettings.ad_banners.items} />
          </div>
        </section>
      )}

      {/* USP Grid */}
      {homepageSettings.usp_grid?.enabled && homepageSettings.usp_grid.items?.length > 0 && (
        <section className="py-20" style={{ backgroundColor: '#FFFDFB' }}>
          <div className="max-w-[1000px] mx-auto px-6 lg:px-10">
            <USPGrid
              title={renderTipTapContent(homepageSettings.usp_grid.title)}
              description={renderTipTapContent(homepageSettings.usp_grid.description)}
              items={homepageSettings.usp_grid.items}
            />
          </div>
        </section>
      )}

      {/* Field Tags */}
      {homepageSettings.field_tags?.enabled && homepageSettings.field_tags.fields?.length > 0 && (
        <FieldTagsSection
          title={homepageSettings.field_tags.title}
          description={homepageSettings.field_tags.description}
          fields={homepageSettings.field_tags.fields}
        />
      )}

      {/* Featured Universities */}
      {homepageSettings.featured_universities?.enabled && (
        <section className="py-20" style={{ backgroundColor: '#FFFDFB' }}>
          <div className="max-w-[1180px] mx-auto px-8 lg:px-12">
            <div className="text-center mb-12">
              <div 
                className="text-3xl md:text-4xl font-bold text-[#111827] mb-4"
                dangerouslySetInnerHTML={{ __html: renderTipTapContent(homepageSettings.featured_universities.title) }}
              />
              <div 
                className="text-lg text-[#4A5568]"
                dangerouslySetInnerHTML={{ __html: renderTipTapContent(homepageSettings.featured_universities.description) }}
              />
            </div>
            {featuredUniversities.length > 0 ? (
              <AutoCarousel autoPlayInterval={4000}>
                {featuredUniversities.map((university) => (
                  <UniversityCard key={university.id} university={university} />
                ))}
              </AutoCarousel>
            ) : (
              <p className="text-center text-[#6B7280]">No featured universities available</p>
            )}
            <div className="text-center mt-8">
              <a 
                href="/universities" 
                className="inline-flex items-center gap-2 text-[#1E40AF] hover:text-[#1E3A8A] font-medium transition-colors"
              >
                View All Universities
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </a>
            </div>
          </div>
        </section>
      )}

      {/* Featured Programs */}
      {homepageSettings.featured_programs?.enabled && (
        <section className="py-20" style={{ backgroundColor: '#F8FAFC' }}>
          <div className="max-w-[1180px] mx-auto px-8 lg:px-12">
            <div className="text-center mb-12">
              <div 
                className="text-3xl md:text-4xl font-bold text-[#111827] mb-4"
                dangerouslySetInnerHTML={{ __html: renderTipTapContent(homepageSettings.featured_programs.title) }}
              />
              <div 
                className="text-lg text-[#4A5568]"
                dangerouslySetInnerHTML={{ __html: renderTipTapContent(homepageSettings.featured_programs.description) }}
              />
            </div>
            {featuredPrograms.length > 0 ? (
              <AutoCarousel autoPlayInterval={5000}>
                {featuredPrograms.map((program) => (
                  <ProgramCard 
                    key={program.id} 
                    program={program} 
                    university={universitiesMap[program.university_id] || program.university_name}
                  />
                ))}
              </AutoCarousel>
            ) : (
              <p className="text-center text-[#6B7280]">No featured programs available</p>
            )}
            <div className="text-center mt-8">
              <a 
                href="/programs" 
                className="inline-flex items-center gap-2 text-[#1E40AF] hover:text-[#1E3A8A] font-medium transition-colors"
              >
                View All Programs
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </a>
            </div>
          </div>
        </section>
      )}

      {/* Scholarship Discovery */}
      {homepageSettings.scholarship_discovery?.enabled && (
        <ScholarshipTagDiscovery
          title={renderTipTapContent(homepageSettings.scholarship_discovery.title)}
          description={renderTipTapContent(homepageSettings.scholarship_discovery.description)}
          courseFields={homepageSettings.scholarship_discovery.course_fields || []}
          universities={homepageSettings.scholarship_discovery.universities || []}
        />
      )}

      {/* Featured Blog Posts */}
      {homepageSettings.featured_blogs?.enabled && blogPosts.length > 0 && (
        <section className="py-20" style={{ backgroundColor: '#FFFDFB' }}>
          <div className="max-w-[1280px] mx-auto px-6 lg:px-10">
            <div className="flex justify-between items-center mb-12">
              <div>
                <div 
                  className="text-3xl md:text-4xl font-bold text-[#111827] mb-2"
                  dangerouslySetInnerHTML={{ __html: renderTipTapContent(homepageSettings.featured_blogs.title) }}
                />
                <div 
                  className="text-lg text-[#4A5568]"
                  dangerouslySetInnerHTML={{ __html: renderTipTapContent(homepageSettings.featured_blogs.description) }}
                />
              </div>
              <a href="/blogs" className="px-6 py-3 bg-white border-2 border-[#1E40AF] text-[#1E40AF] font-semibold rounded-lg hover:bg-[#1E40AF] hover:text-white transition-colors">
                View All Articles
              </a>
            </div>
            <BlogPostCarousel posts={blogPosts.slice(0, homepageSettings.featured_blogs.max_items || 4)} />
          </div>
        </section>
      )}

      {/* Final CTA */}
      {homepageSettings.final_cta?.enabled && (
        <FinalCTASection
          headline={renderTipTapContent(homepageSettings.final_cta.headline)}
          description={renderTipTapContent(homepageSettings.final_cta.description)}
          primaryButtonText={homepageSettings.final_cta.primary_button_text}
          primaryButtonLink={homepageSettings.final_cta.primary_button_url}
          secondaryButtonText={homepageSettings.final_cta.secondary_button_text}
          secondaryButtonLink={homepageSettings.final_cta.secondary_button_url}
          imageUrl={homepageSettings.final_cta.image_url}
        />
      )}

      <Footer />
      <WhatsAppCTA />
    </div>
  );
};

export default Home;
